import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { SettingsDoc } from "@scenix/core";
import { ApiError, api } from "../lib/api";
import { RECOVERY_WINDOW_DAYS } from "../lib/recovery";
import { Crumbs } from "../components/Crumbs";
import { Markdown } from "../components/Markdown";
import { RecoveryCountdown } from "../components/RecoveryCountdown";

/**
 * A space's settings screen: house rules, client preferences, whatever is
 * always true of it rather than of one project (ADR 000037).
 *
 * List on the left, one document on the right, the same shape as account
 * settings. Which document is open is a query parameter, so a link to
 * "the rules" survives a reload and can be shared.
 */

/** The three names most spaces want. Nothing here is special to the system: a
 * space that wants a fourth adds it by typing it, the same as these. */
const SUGGESTED = [
  {
    slug: "info",
    title: "Info",
    hint: "What this space is, who it is for, and what is being made.",
  },
  {
    slug: "rules",
    title: "Rules",
    hint: "Constraints that always apply: house style, and what never to do.",
  },
  { slug: "tips", title: "Tips", hint: "Things that are true and easy to forget." },
];

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

function validSlug(slug: string): boolean {
  return slug.length >= 2 && slug.length <= 60 && SLUG_PATTERN.test(slug);
}

/**
 * The body without its leading frontmatter block, for display only.
 *
 * `body` keeps the frontmatter inline on the wire, so editing round-trips it
 * exactly (`domain.SettingsDoc.Body`'s own doc comment). But `meta` already
 * shows those values as a table, and Markdown reads a lone `---` as a setext
 * heading underline: rendering the raw body a second time turned "foo: bar"
 * into a heading rather than showing nothing extra. Mirrors the block
 * detection in `domain.ParseFrontmatter` (Go); a shape it does not recognise
 * is left alone, same as there.
 */
function withoutFrontmatter(body: string): string {
  const bom = String.fromCodePoint(0xfeff);
  const trimmed = body.replace(new RegExp(`^[${bom} \\t\\r\\n]+`), "");
  if (!trimmed.startsWith("---")) return body;

  let rest = trimmed.slice(3);
  if (rest.startsWith("\r")) rest = rest.slice(1);
  if (rest.startsWith("\n")) rest = rest.slice(1);

  const end = rest.indexOf("\n---");
  if (end < 0) return body;

  return rest.slice(end + 4).replace(/^\r?\n/, "");
}

export function SpaceSettingsRoute() {
  const { spaceId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const selected = adding ? "" : (params.get("doc") ?? "");

  const space = useQuery({
    queryKey: ["space", spaceId],
    queryFn: () => api.getSpace(spaceId as never),
  });

  const list = useQuery({
    // A distinct key from an individual document's `["settings", spaceId,
    // slug]`, deliberately: react-query's invalidation matches by prefix, so
    // sharing one would mean refreshing this list also refetches whichever
    // document happens to be open, undoing the write that was just made.
    queryKey: ["settings-list", spaceId],
    queryFn: () => api.listSettings(spaceId as never),
  });

  function open(slug: string) {
    setAdding(false);
    setParams(slug ? { doc: slug } : {});
  }

  function refreshList() {
    return queryClient.invalidateQueries({ queryKey: ["settings-list", spaceId] });
  }

  if (space.isPending || list.isPending) return <p role="status">Loading…</p>;

  if (space.error) {
    const missing = space.error instanceof ApiError && space.error.isNotFound;
    return (
      <section className="empty">
        <h1>{missing ? "No such space" : "Could not load this space"}</h1>
        <p className="muted">
          {missing
            ? "It may have been deleted, or you may not be a member of it."
            : describe(space.error)}
        </p>
        <p>
          <Link to="/spaces">Back to your spaces</Link>
        </p>
      </section>
    );
  }

  if (list.error) {
    return (
      <section className="empty">
        <h1>Could not load this space's settings</h1>
        <p className="muted">{describe(list.error)}</p>
      </section>
    );
  }

  // Editing needs admin: house rules are a judgement somebody owns, and the
  // thing every prompt in the space carries should not be editable by
  // everybody who can upload a clip (ADR 000036, ADR 000037).
  const mayEdit = space.data.role === "admin" || space.data.role === "owner";
  const existingSlugs = new Set(list.data.map((doc) => doc.slug));

  return (
    <section>
      <header className="page-head">
        <Crumbs spaceId={spaceId} />
        <h1>Settings</h1>
        <p className="muted">What is always true of this space, not what one project is.</p>
      </header>

      <div className="settings">
        <nav className="settings-nav" aria-label="Settings documents">
          {list.data.map((doc) => (
            <Link
              key={doc.slug}
              to={`?doc=${encodeURIComponent(doc.slug)}`}
              className={doc.slug === selected ? "active" : ""}
            >
              {doc.title}
            </Link>
          ))}
          {mayEdit && (
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setAdding(true);
                setShowDeleted(false);
                setParams({});
              }}
            >
              {adding ? "Adding…" : "+ Add"}
            </button>
          )}
          {mayEdit && (
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setShowDeleted(true);
                setAdding(false);
              }}
            >
              Deleted
            </button>
          )}
        </nav>

        <div className="settings-body">
          {showDeleted ? (
            <DeletedSettingsPanel
              spaceId={spaceId}
              onClose={() => setShowDeleted(false)}
              onRestored={refreshList}
            />
          ) : adding ? (
            <AddDocPanel
              existingSlugs={existingSlugs}
              onPick={(slug) => open(slug)}
              onCancel={() => setAdding(false)}
            />
          ) : selected ? (
            <SettingsDocPanel
              spaceId={spaceId}
              slug={selected}
              mayEdit={mayEdit}
              onSaved={refreshList}
              onDeleted={() => {
                void refreshList();
                open("");
              }}
            />
          ) : list.data.length === 0 ? (
            <p className="muted">
              {mayEdit
                ? "Nothing here yet. Add info, rules or tips for this space."
                : "Nothing here yet."}
            </p>
          ) : (
            <p className="muted">Pick a document from the list.</p>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Settings documents inside the recovery window (issue #8). A separate view
 * rather than a filter on the ordinary list: a deleted document is not
 * addressable the normal way, so it gets its own actions instead of a link
 * into a document that 404s until restored.
 */
function DeletedSettingsPanel({
  spaceId,
  onClose,
  onRestored,
}: {
  spaceId: string;
  onClose: () => void;
  onRestored: () => void;
}) {
  const queryClient = useQueryClient();

  const deleted = useQuery({
    queryKey: ["settings-list", spaceId, "deleted"],
    queryFn: () => api.listSettings(spaceId as never, { deleted: true }),
  });

  const restore = useMutation({
    mutationFn: (slug: string) => api.restoreSettings(spaceId as never, slug),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings-list", spaceId] });
      onRestored();
    },
  });

  return (
    <div>
      <div className="section-bar">
        <h2 className="section-head">Deleted</h2>
        <button type="button" className="ghost" onClick={onClose}>
          Back
        </button>
      </div>

      {/* What a space wrote down about how it works is not recoverable from
          anywhere else either, so this panel owes the same deadline the corpus
          shows (ADR 000055, ADR 000037). */}
      <p className="muted">
        A deleted document can be brought back for {RECOVERY_WINDOW_DAYS} days. After that it is
        removed for good, and nothing here can bring it back.
      </p>

      {deleted.isPending ? (
        <p role="status">Loading…</p>
      ) : deleted.error ? (
        <p className="error">{describe(deleted.error)}</p>
      ) : deleted.data.length === 0 ? (
        <p className="muted">Nothing deleted right now.</p>
      ) : (
        <ul className="settings-nav">
          {deleted.data.map((doc) => (
            <li key={doc.slug} className="compose-row">
              <span>{doc.title}</span>
              <RecoveryCountdown deletedAt={doc.deletedAt} />
              <button
                type="button"
                className="ghost"
                disabled={restore.isPending}
                onClick={() => restore.mutate(doc.slug)}
              >
                Restore
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Choosing the name a new document is addressed by. The name is the id, so it
 * is the one thing that cannot change once picked. */
function AddDocPanel({
  existingSlugs,
  onPick,
  onCancel,
}: {
  existingSlugs: Set<string>;
  onPick: (slug: string) => void;
  onCancel: () => void;
}) {
  const [custom, setCustom] = useState("");
  const suggestions = SUGGESTED.filter((s) => !existingSlugs.has(s.slug));
  const customValid = validSlug(custom) && !existingSlugs.has(custom);

  return (
    <div>
      <h2 className="section-head">Add a document</h2>

      {suggestions.length > 0 && (
        <div className="compose-row">
          {suggestions.map((s) => (
            <button key={s.slug} type="button" className="ghost" onClick={() => onPick(s.slug)}>
              {s.title}
            </button>
          ))}
        </div>
      )}

      <form
        className="compose-row"
        onSubmit={(event) => {
          event.preventDefault();
          if (customValid) onPick(custom);
        }}
      >
        <input
          aria-label="Custom name"
          placeholder="lowercase-with-hyphens"
          value={custom}
          onChange={(event) => setCustom(event.target.value.trim().toLowerCase())}
          maxLength={60}
        />
        <button type="submit" disabled={!customValid}>
          Use this name
        </button>
      </form>

      {custom.length > 0 && !validSlug(custom) && (
        <p className="muted">Lowercase letters, digits and single hyphens, 2 to 60 characters.</p>
      )}
      {custom.length > 0 && validSlug(custom) && existingSlugs.has(custom) && (
        <p className="muted">There is already a document named &ldquo;{custom}&rdquo;.</p>
      )}

      <button type="button" className="ghost" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

/**
 * One document, fetched. Split from `SettingsDocEditor` so the editor only
 * ever mounts once the fetch has settled: at that point `doc`/`isNew` are
 * known for good, and the editor's local draft state can read them straight
 * into `useState`'s initial value instead of an effect syncing them in after
 * the fact.
 */
function SettingsDocPanel({
  spaceId,
  slug,
  mayEdit,
  onSaved,
  onDeleted,
}: {
  spaceId: string;
  slug: string;
  mayEdit: boolean;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const doc = useQuery({
    queryKey: ["settings", spaceId, slug],
    queryFn: () => api.getSettings(spaceId as never, slug),
    retry: false,
  });

  if (doc.isPending) return <p role="status">Loading…</p>;

  const isNew = doc.error instanceof ApiError && doc.error.isNotFound;
  if (doc.error && !isNew) {
    return <p className="error">{describe(doc.error)}</p>;
  }

  // Everything but the not-found case is ruled out above, so this is either a
  // loaded document or, for `isNew`, undefined.
  const data = doc.data ?? null;

  return (
    <SettingsDocEditor
      // Remounts on a different document, and once more when a create or
      // edit lands a new revision, which is what resets the draft without
      // ever needing to sync it in by hand.
      key={`${slug}:${data ? data.revision : "new"}`}
      spaceId={spaceId}
      slug={slug}
      doc={data}
      mayEdit={mayEdit}
      refetch={doc.refetch}
      onSaved={onSaved}
      onDeleted={onDeleted}
    />
  );
}

/**
 * One document: read it, or edit it. A document that does not exist yet
 * (a name just picked from `AddDocPanel`, so `doc` is null) opens straight
 * into edit mode, since creating one and editing one are the same write
 * (ADR 000037).
 */
function SettingsDocEditor({
  spaceId,
  slug,
  doc,
  mayEdit,
  refetch,
  onSaved,
  onDeleted,
}: {
  spaceId: string;
  slug: string;
  doc: SettingsDoc | null;
  mayEdit: boolean;
  refetch: () => Promise<{ data: SettingsDoc | undefined }>;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const isNew = doc === null;

  const [editing, setEditing] = useState(isNew);
  const [title, setTitle] = useState(doc?.title ?? "");
  const [body, setBody] = useState(doc?.body ?? "");

  const save = useMutation({
    mutationFn: () =>
      api.putSettings(spaceId as never, slug, {
        title: title.trim(),
        body,
        baseRevision: doc?.revision ?? 0,
      }),
    onSuccess: (updated: SettingsDoc) => {
      queryClient.setQueryData(["settings", spaceId, slug], updated);
      setEditing(false);
      onSaved();
    },
  });

  const remove = useMutation({
    mutationFn: () => api.deleteSettings(spaceId as never, slug),
    onSuccess: () => onDeleted(),
  });

  if (editing) {
    const conflict = save.error instanceof ApiError && save.error.isConflict;
    return (
      <form
        className="compose settings-doc-edit"
        onSubmit={(event) => {
          event.preventDefault();
          if (title.trim()) save.mutate();
        }}
      >
        <h2 className="section-head">{isNew ? `New: ${slug}` : title}</h2>

        <label htmlFor="settings-doc-title">Title</label>
        <input
          id="settings-doc-title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={200}
          autoFocus
        />

        <label htmlFor="settings-doc-body">Body</label>
        <textarea
          id="settings-doc-body"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={16}
        />

        {save.error && (
          <p role="alert" className="error">
            {conflict
              ? "Somebody else edited this since you opened it. Reload to see their version, then try again."
              : describe(save.error)}
          </p>
        )}

        <div className="compose-row">
          <button type="submit" disabled={!title.trim() || save.isPending}>
            {save.isPending ? "Saving…" : isNew ? "Create" : "Save"}
          </button>
          {conflict && (
            <button
              type="button"
              className="ghost"
              onClick={async () => {
                const fresh = await refetch();
                if (fresh.data) {
                  setTitle(fresh.data.title);
                  setBody(fresh.data.body);
                }
                save.reset();
              }}
            >
              Reload the latest version
            </button>
          )}
          {!isNew && (
            <button type="button" className="ghost" onClick={() => setEditing(false)}>
              Cancel
            </button>
          )}
        </div>
      </form>
    );
  }

  if (!doc) return null;

  const metaEntries = Object.entries(doc.meta);

  return (
    <>
      <div className="section-bar">
        <h2 className="section-head">{doc.title}</h2>
        {mayEdit && (
          <button type="button" className="ghost" onClick={() => setEditing(true)}>
            Edit
          </button>
        )}
      </div>

      {metaEntries.length > 0 && (
        <dl className="facts">
          {metaEntries.map(([key, value]) => (
            <div key={key} className="fact-row">
              <dt>{key}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}

      <Markdown>{withoutFrontmatter(doc.body)}</Markdown>
      <p className="muted">Revision {doc.revision}</p>

      {mayEdit && (
        <p className="danger-zone">
          <button
            type="button"
            className="ghost danger"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`Delete "${doc.title}"? You can restore it from Deleted later.`)) {
                remove.mutate();
              }
            }}
          >
            {remove.isPending ? "Deleting…" : "Delete"}
          </button>
        </p>
      )}
    </>
  );
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.userMessage;
  return error instanceof Error ? error.message : "Something went wrong.";
}
